/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as d3 from 'd3';

import {
  PerformanceAssetAllocation,
  PerformanceChart,
  PerformanceFilterRangeValue,
  PerformanceGraphItem,
  PerformanceType
} from '@shared/models/Home';

import {
  createOrUpdateDotGroup,
  createSvg,
  generateBottomAxis,
  getBisect,
  getChartMaxMinValues,
  getChartStartEndValues,
  getCssValueFromDom,
  getPerformanceTooltip,
  getXScale,
  getYScale,
  PerformanceColors,
  renderLine,
  getChartQuickFilters,
  getClosestPortfolioUpdateItem
} from './home-performance-chart.util';

const PORTFOLIO_UPDATE_SNAP_THRESHOLD = 7;
const THREE_ROW_HEIGHT = 84;
const FOUR_ROW_HEIGHT = 100;
const EVENT_TOOLTIP_WIDTH = 148;

@Component({
  selector: 'app-home-performance-chart',
  templateUrl: './home-performance-chart.component.html',
  styleUrls: ['./home-performance-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HomePerformanceChartComponent implements OnInit, OnChanges {
  @Input() currency: string;
  @Input() language: string;
  @Input() kind: '3b' | '3a' | 'kid' | 'ci';
  @Input() hasPortfolioUpdatesFlag: boolean;
  @Input() chartData: PerformanceGraphItem[];
  @Input() firstPerformanceDate: string;
  @Input() performance: PerformanceChart;
  @Output() filterChange: EventEmitter<PerformanceFilterRangeValue> = new EventEmitter<PerformanceFilterRangeValue>();
  @Output() typeChange: EventEmitter<PerformanceType> = new EventEmitter<PerformanceType>();
  @Output() openPerfSettings: EventEmitter<{ showBenchmark: boolean }> = new EventEmitter<{ showBenchmark: boolean }>();
  @Output() graphInteraction: EventEmitter<{
    cta: 'date_scrubbed' | 'date_tapped' | 'portfolio_change_card_opened';
    range: PerformanceFilterRangeValue;
  }> = new EventEmitter<{
    cta: 'date_scrubbed' | 'date_tapped' | 'portfolio_change_card_opened';
    range: PerformanceFilterRangeValue;
  }>();

  performanceFilters: { id: string; value: string | number }[];

  height = 152;
  axisBottom = null;
  svg: d3.Selection<d3.BaseType, unknown, HTMLElement, unknown>;
  xScale;
  yScale;
  xStartScale;
  xEndScale;
  width = window.innerWidth;
  showTooltip = false;
  showPortfolioUpdateTooltip = false;
  showInfo = false;
  is3bProduct = true;
  lastSelectedAccount = null;
  eventType: 'date_scrubbed' | 'date_tapped' | null = null;
  hasPortfolioUpdate: boolean | null = null;

  tabs: { value: string; id: string }[];

  tooltipData: {
    date: Date;
    performance: { percentage: number }[];
    stockSplit: { bondPercent: number; cashPercent: number; fundPercent: number; stockPercent: number };
    balance: { amount: number; moneyInOut: number; moneyReturn: number };
  };

  portfolioUpdateTooltip: {
    data: null | { previous: PerformanceAssetAllocation; current: PerformanceAssetAllocation };
    position: { left: number; top: number };
    tooltipHeight: number;
  };

  infoList = {
    title: false,
    benchmark: false
  };

  get isPerformanceChart(): boolean {
    return this.performance.chartType === PerformanceType.PERFORMANCE;
  }

  get performanceChartColor() {
    return this.isPerformanceChart ? PerformanceColors.RETURNS : PerformanceColors.BALANCE;
  }

  constructor(
    private cd: ChangeDetectorRef,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.tabs = [
      { value: this.translateService.instant('PERFORMANCE.performanceChart.chartSwitcher.returnsLabel'), id: PerformanceType.PERFORMANCE },
      {
        value: this.translateService.instant('PERFORMANCE.performanceChart.chartSwitcher.balanceLabel', { currency: this.currency.toUpperCase() }),
        id: PerformanceType.BALANCE
      }
    ];
    this.svg = createSvg('.performance-chart', this.width, this.height);

    this.is3bProduct = this.kind !== '3a';
    this.drawChart(this.chartData, false, this.performance?.showBenchmark ?? false, this.performance?.showPortfolioChanges);
    this.lastSelectedAccount = this.kind;
  }

  ngOnChanges(changes: SimpleChanges): void {
    let shouldRedraw = false;

    if (changes.kind && !changes.chartData.firstChange) {
      this.is3bProduct = changes.kind?.currentValue !== '3a';
      // if account is not the same hide the tooltip
      if (changes.kind.currentValue !== this.lastSelectedAccount) {
        this.hideRules();
      }
    }

    if (changes.language && !changes.language.firstChange) {
      shouldRedraw = true;
    }

    if (changes.chartData && !changes.chartData.firstChange) {
      shouldRedraw = true;
    }

    if (
      changes.performance &&
      !changes.performance.firstChange &&
      (changes.performance.previousValue?.showPortfolioChanges !== changes.performance.currentValue?.showPortfolioChanges ||
        changes.performance.previousValue?.showBenchmark !== changes.performance.currentValue?.showBenchmark)
    ) {
      shouldRedraw = true;
    }

    if (shouldRedraw) {
      this.drawChart(this.chartData, true, this.performance?.showBenchmark ?? false, this.performance?.showPortfolioChanges);
    }
  }

  drawChart(chartData: PerformanceGraphItem[], updated = false, showBenchmark = false, showPortfolioChanges = false) {
    this.performanceFilters = getChartQuickFilters(this.performance.lastExtendedRange);
    this.cd.markForCheck();

    if (chartData.length === 0) {
      return;
    }

    const { min, max } = getChartMaxMinValues(chartData);

    const chartMargin = Number(getCssValueFromDom('--spacing-4').replace('px', ''));

    // scale the graph based on the size
    this.xScale = getXScale(chartData, chartMargin, this.width - chartMargin);
    this.yScale = getYScale(min, max, this.height);

    // start & end lines data
    const { startItems, endItems } = getChartStartEndValues(chartData);
    this.xStartScale = getXScale(startItems, 0, chartMargin - 1);
    this.xEndScale = getXScale(endItems, this.width - chartMargin + 1, this.width);

    // Transition
    const duration = 500;
    const t = d3.transition().duration(duration);

    const zeroLine = this.svg.selectAll('.performance-zero').data([0]);
    zeroLine.data([0]).join(
      (enter) =>
        enter
          .append('line')
          .attr('class', 'performance-zero')
          .attr('x1', 0)
          .attr('x2', this.width)
          .attr('y1', this.yScale(this.isPerformanceChart ? 0 : min))
          .attr('y2', this.yScale(this.isPerformanceChart ? 0 : min))
          .attr('stroke', `var(${PerformanceColors.CORE_NEUTRAL_4})`)
          .style('stroke-dasharray', '2, 2'),
      (update) =>
        update
          .transition(t)
          .attr('x1', 0)
          .attr('x2', this.width)
          .attr('y1', this.yScale(this.isPerformanceChart ? 0 : min))
          .attr('y2', this.yScale(this.isPerformanceChart ? 0 : min)),
      (exit) => exit.remove()
    );

    const lines = [
      { name: 'line', data: chartData, xScaleFn: this.xScale },
      { name: 'start', data: startItems, xScaleFn: this.xStartScale },
      { name: 'end', data: endItems, xScaleFn: this.xEndScale }
    ];

    lines.forEach(({ name, data, xScaleFn }: { name: string; data: PerformanceGraphItem[]; xScaleFn: (value: any) => any }) => {
      if (this.is3bProduct && showBenchmark) {
        // render benchmark lines
        renderLine(
          min,
          `benchmark-${name}`,
          this.svg,
          this.isPerformanceChart ? data : [],
          PerformanceColors.BENCHMARK,
          null,
          1.25,
          xScaleFn,
          this.yScale,
          'benchmarkValue'
        );
        // update z-index
        this.svg.select(`.benchmark-${name}`).lower();
      } else {
        this.svg.select(`.benchmark-${name}`).remove();
      }

      if (!this.isPerformanceChart) {
        // deposit withdrawal lines
        // render performance lines
        renderLine(
          min,
          `moneyInOut-${name}`,
          this.svg,
          !this.isPerformanceChart ? data : [],
          PerformanceColors.BENCHMARK,
          null,
          1.25,
          xScaleFn,
          this.yScale,
          'moneyInOut',
          'curveStepAfter'
        );
      } else {
        this.svg.select(`.moneyInOut-${name}`).remove();
      }

      // render performance lines
      renderLine(
        min,
        `performance-${name}`,
        this.svg,
        data,
        this.performanceChartColor,
        this.isPerformanceChart ? PerformanceColors.RETURNS : PerformanceColors.BALANCE,
        2,
        xScaleFn,
        this.yScale,
        'value'
      );
    });

    // Portfolio changes
    if (showPortfolioChanges) {
      const portfolioUpdateItems = chartData.filter((item) => item.portfolioUpdate);
      this.svg.selectAll('.perf-graph__updates').data([0]).enter().append('g').attr('class', 'perf-graph__updates');

      this.svg
        .select('.perf-graph__updates')
        .selectAll('.perf-graph__update')
        .data(portfolioUpdateItems)
        .join(
          (enter) =>
            enter
              .append('circle')
              .attr('class', 'perf-graph__update')
              .attr('cx', (d: PerformanceGraphItem) => this.xScale(d.date))
              .attr('cy', (d: PerformanceGraphItem) => this.yScale(d.value))
              .attr('fill', `var(${PerformanceColors.BACKGROUND})`)
              .attr('stroke', `var(${PerformanceColors.EVENT})`)
              .attr('stroke-width', 2)
              .attr('r', 0)
              .transition(t)
              .attr('r', 4),
          (update) =>
            update
              .attr('cx', (d: PerformanceGraphItem) => this.xScale(d.date))
              .attr('cy', (d: PerformanceGraphItem) => this.yScale(d.value))
              .attr('r', 4),
          (exit) => exit.transition().duration(200).attr('r', 0).remove()
        );
    } else {
      this.svg.select('.perf-graph__updates').remove();
      this.showPortfolioUpdateTooltip = false;
    }

    // Vertical ruler
    const perfRuler = this.svg.selectAll('.perf-graph__ruler').data([0]);
    perfRuler
      .enter()
      .append('g')
      .attr('class', 'perf-graph__ruler')
      .style('opacity', 0)
      .append('line')
      .attr('y1', this.height)
      .attr('y2', 0)
      .attr('stroke', `var(${PerformanceColors.CORE_BRAND_3_3})`);

    // xBottomAxis
    if (this.axisBottom !== null) {
      this.svg.select('.perf-labels').remove();
    }

    // Money In/Out - Benchmark dot
    if (!this.isPerformanceChart || (this.is3bProduct && showBenchmark)) {
      createOrUpdateDotGroup(
        this.svg,
        'bench-graph__dot',
        'bench-dot',
        `var(${PerformanceColors.BACKGROUND})`,
        `var(${PerformanceColors.BENCHMARK})`,
        1.4
      );
    } else {
      this.svg.select('.bench-graph__dot').remove();
    }

    // Ruler dot
    createOrUpdateDotGroup(this.svg, 'perf-graph__dot', 'perf-dot', `var(${PerformanceColors.BACKGROUND})`, `var(${this.performanceChartColor})`, 3);

    this.axisBottom = this.svg
      .append('g')
      .attr('class', 'perf-labels')
      .attr('transform', `translate (0, ${this.height})`)
      .call(generateBottomAxis(this.xScale, chartData, this.language))
      .call((g) => g.select('.domain').remove())
      .call((g) => g.selectAll('.tick line').remove());

    const nodes = this.axisBottom.selectAll('text').attr('class', 'ds-ui-3').style('fill', `var(${PerformanceColors.BENCHMARK})`).nodes();

    d3.select(nodes[0]).attr('text-anchor', 'start');
    d3.select(nodes[nodes.length - 1]).attr('text-anchor', 'end');

    this.addEvents(chartData, this.svg, this.xScale, updated, showPortfolioChanges);
  }

  addEvents(
    chartData: PerformanceGraphItem[],
    svg: d3.Selection<d3.BaseType, unknown, HTMLElement, unknown>,
    xScaleFn: (param) => any,
    updated: boolean,
    showPortfolioChanges: boolean
  ) {
    if (updated) {
      this.svg.select('.performance-rect').remove();
    }

    const eventRect = this.svg
      .append('rect')
      .attr('class', 'performance-rect')
      .attr('width', this.width)
      .attr('height', this.height)
      .attr('fill', 'transparent');

    eventRect.on('mousemove touchmove', (event) => {
      this.viewChart(event, eventRect, chartData, xScaleFn, showPortfolioChanges);
    });

    eventRect.on('mouseout touchend', (event: MouseEvent) => {
      this.hideRules();
      this.showPortfolioUpdateTooltip = false;

      if (this.eventType === null && event.type === 'mouseout') {
        this.showLastItem(chartData, xScaleFn);
      }

      if (this.eventType === 'date_scrubbed') {
        this.showLastItem(chartData, xScaleFn);
        this.graphInteraction.emit({ cta: this.hasPortfolioUpdate ? 'portfolio_change_card_opened' : this.eventType, range: this.performance.range });
        this.eventType = null;
        this.hasPortfolioUpdate = null;
      }
      this.cd.detectChanges();
    });

    this.showLastItem(chartData, xScaleFn);
  }

  hideRules() {
    this.showTooltip = false;
    d3.select('.perf-graph__ruler').attr('transform', 'translate(0, 0)').style('opacity', 0);
    d3.select('.perf-graph__dot').attr('transform', `translate(0, 0)`).style('opacity', 0);

    if (this.is3bProduct || (!this.is3bProduct && !this.isPerformanceChart)) {
      d3.select('.bench-graph__dot').attr('transform', `translate(0, 0)`).style('opacity', 0);
    }
    this.cd.detectChanges();
  }

  showLastItem(chartData: PerformanceGraphItem[], xScaleFn: (param) => any) {
    const lastItem = chartData[chartData.length - 1];
    const { date, value, benchmarkValue, moneyInOut, moneyReturn, differenceToBenchmark, bondPercent, cashPercent, fundPercent, stockPercent } =
      lastItem;
    this.drawTooltip({
      date,
      value,
      benchmarkValue,
      moneyInOut,
      moneyReturn,
      differenceToBenchmark,
      bondPercent,
      cashPercent,
      fundPercent,
      stockPercent,
      xScaleFn
    });
  }

  viewChart(event, elem, chartData: PerformanceGraphItem[], xScaleFn: (param) => any, showPortfolioChanges: boolean) {
    // distinguish event touchmove or mouse move
    const expectedEv = window.TouchEvent && event instanceof TouchEvent ? event.touches[0] : event;

    this.eventType = window.TouchEvent && event instanceof TouchEvent ? 'date_scrubbed' : 'date_tapped';

    const [mx] = d3.pointer(expectedEv, elem.node());

    const closestPortfolioUpdate = getClosestPortfolioUpdateItem(mx, chartData, xScaleFn);

    const activeItem =
      showPortfolioChanges && closestPortfolioUpdate && closestPortfolioUpdate.distance <= PORTFOLIO_UPDATE_SNAP_THRESHOLD
        ? closestPortfolioUpdate.item
        : getBisect(mx, chartData, xScaleFn);

    this.hasPortfolioUpdate = activeItem.portfolioUpdate !== null && showPortfolioChanges;

    if (this.eventType === 'date_tapped') {
      this.graphInteraction.emit({ cta: this.hasPortfolioUpdate ? 'portfolio_change_card_opened' : this.eventType, range: this.performance.range });
      this.eventType = null;
      this.hasPortfolioUpdate = null;
    }

    this.drawTooltip({
      date: activeItem.date,
      value: activeItem.value,
      benchmarkValue: activeItem.benchmarkValue,
      moneyInOut: activeItem.moneyInOut,
      moneyReturn: activeItem.moneyReturn,
      differenceToBenchmark: activeItem.differenceToBenchmark,
      bondPercent: activeItem.bondPercent,
      cashPercent: activeItem.cashPercent,
      fundPercent: activeItem.fundPercent,
      stockPercent: activeItem.stockPercent,
      xScaleFn,
      hasActivePortfolioUpdate: activeItem.portfolioUpdate !== null && showPortfolioChanges
    });

    if (activeItem.portfolioUpdate !== null && showPortfolioChanges) {
      this.drawPortfolioUpdateTooltip(activeItem.portfolioUpdate, activeItem.date, activeItem.value, xScaleFn);
      this.showPortfolioUpdateTooltip = true;
    } else {
      this.showPortfolioUpdateTooltip = false;
    }
    this.cd.detectChanges();
  }

  drawTooltip({
    date,
    value,
    benchmarkValue,
    moneyInOut,
    moneyReturn,
    differenceToBenchmark,
    bondPercent,
    cashPercent,
    fundPercent,
    stockPercent,
    xScaleFn,
    hasActivePortfolioUpdate = false
  }) {
    this.showTooltip = !!date;

    this.tooltipData = {
      ...this.tooltipData,
      date: date as Date,
      ...(this.isPerformanceChart && {
        performance: getPerformanceTooltip(value as number, benchmarkValue as number, differenceToBenchmark as number),
        stockSplit: { bondPercent, cashPercent, fundPercent, stockPercent }
      }),
      ...(!this.isPerformanceChart && {
        balance: {
          amount: value,
          moneyInOut,
          moneyReturn
        }
      })
    };

    const xScaledDate = xScaleFn(date);

    d3.select('.perf-graph__ruler')
      .attr('transform', `translate(${(xScaledDate as number) + 0.5}, 0)`)
      .style('opacity', 1);

    if (!this.isPerformanceChart || (this.is3bProduct && this.performance?.showBenchmark)) {
      d3.select('.bench-graph__dot')
        .attr('transform', `translate(${xScaledDate as number},  ${this.yScale(this.isPerformanceChart ? benchmarkValue : moneyInOut)})`)
        .style('opacity', 1);
    }

    d3.select('.perf-graph__dot')
      .attr('transform', `translate(${xScaledDate as number},  ${this.yScale(value)})`)
      .select('.perf-dot')
      .attr('fill', hasActivePortfolioUpdate ? 'var(--color-core-neutral-10)' : `var(--ion-color-light)`)
      .attr('stroke', hasActivePortfolioUpdate ? 'var(--color-core-neutral-10)' : `var(${this.performanceChartColor})`);

    d3.select('.perf-graph__dot').style('opacity', 1);

    this.cd.detectChanges();
  }

  drawPortfolioUpdateTooltip(
    portfolioUpdate: null | { previous: PerformanceAssetAllocation; current: PerformanceAssetAllocation },
    date: string,
    value: number,
    xScaleFn: (param) => any
  ) {
    const eventX = xScaleFn(date) as number;
    const eventY = this.yScale(value) as number;

    const rectNode = this.svg.select<SVGRectElement>('.performance-rect').node();

    const rectBox = rectNode.getBBox();
    const gap = 20;
    const hasStock = portfolioUpdate.previous.stock_percentage !== 0 || portfolioUpdate.current.stock_percentage !== 0;
    const hasFund = portfolioUpdate.previous.fund_percentage !== 0 || portfolioUpdate.current.fund_percentage !== 0;
    const tooltipHeight = hasStock && hasFund ? FOUR_ROW_HEIGHT : THREE_ROW_HEIGHT;

    const hasSpaceRight = eventX + gap + EVENT_TOOLTIP_WIDTH <= rectBox.x + rectBox.width;

    const preferredLeft = hasSpaceRight ? eventX + gap : eventX - gap - EVENT_TOOLTIP_WIDTH;
    const minLeft = rectBox.x;
    const maxLeft = rectBox.x + rectBox.width - EVENT_TOOLTIP_WIDTH;

    const minTop = rectBox.y;
    const maxTop = rectBox.y + rectBox.height - tooltipHeight;

    const left = Math.min(Math.max(preferredLeft, minLeft), maxLeft);
    const top = Math.min(Math.max(eventY - tooltipHeight / 2, minTop), maxTop);

    this.portfolioUpdateTooltip = {
      data: portfolioUpdate,
      position: { left, top },
      tooltipHeight
    };
  }

  onFilterChange(filter: PerformanceFilterRangeValue) {
    if (filter === this.performance.range || this.performance.loading) return;
    this.filterChange.emit(filter);
  }

  onOpenPerfSettings(showBenchmark: boolean) {
    this.openPerfSettings.emit({ showBenchmark });
  }

  onTypeChange(selectedTab: PerformanceType) {
    if (selectedTab === this.performance.chartType || this.performance.loading) return;
    this.hideRules();
    this.typeChange.emit(selectedTab);
  }

  onToggleInfo(item: string) {
    this.infoList[item] = !this.infoList[item];
  }
}
